import { useState, useEffect, useCallback } from 'react';
import { gpxTraceService, GPXTrace, GPXTracesResponse } from '../services/gpxTraceService';

export interface UseGPXTracesResult {
    traces: GPXTrace[];
    isLoading: boolean;
    error: string | null;
    totalTraces: number;
    hasMore: boolean;
    refreshTraces: () => Promise<void>;
    loadMoreTraces: () => Promise<void>;
    uploadTrace: (file: File, description?: string) => Promise<void>;
    deleteTrace: (tracePath: string) => Promise<void>;
    updateTrace: (tracePath: string, updates: Partial<Pick<GPXTrace, 'original_name' | 'description'>>) => Promise<void>;
}

export const useGPXTraces = (): UseGPXTracesResult => {
    const [traces, setTraces] = useState<GPXTrace[]>([]);
    const [isLoading, setIsLoading] = useState(false);    const [error, setError] = useState<string | null>(null);
    const [totalTraces, setTotalTraces] = useState(0);
    const [currentSkip, setCurrentSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const limit = 20;

    const loadTraces = useCallback(async (skip = 0, append = false) => {
        setIsLoading(true);
        setError(null);

        try {
            const response: GPXTracesResponse = await gpxTraceService.getUserTraces(skip, limit);

            if (append) {
                setTraces(prev => [...prev, ...response.items]);
            } else {
                setTraces(response.items);
            }

            setTotalTraces(response.total);
            setCurrentSkip(skip);
            setHasMore(skip + response.items.length < response.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors du chargement des traces');
        } finally {
            setIsLoading(false);
        }
    }, [limit]);

    const refreshTraces = useCallback(async () => {
        await loadTraces(0, false);
    }, [loadTraces]);

    const loadMoreTraces = useCallback(async () => {
        if (!hasMore || isLoading) return;
        await loadTraces(currentSkip + limit, true);
    }, [hasMore, isLoading, currentSkip, limit, loadTraces]);

    const uploadTrace = useCallback(async (file: File, description?: string) => {
        setIsLoading(true);
        setError(null);

        try {
            await gpxTraceService.uploadTrace(file, description);
            // Recharger les traces après l'upload
            await refreshTraces();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload de la trace');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [refreshTraces]);

    const deleteTrace = useCallback(async (tracePath: string) => {
        setIsLoading(true);
        setError(null);

        try {
            await gpxTraceService.deleteTrace(tracePath);
            // Retirer la trace de la liste locale
            setTraces(prev => prev.filter(trace => trace.path !== tracePath));
            setTotalTraces(prev => prev - 1);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la suppression de la trace');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const updateTrace = useCallback(async (tracePath: string, updates: Partial<Pick<GPXTrace, 'original_name' | 'description'>>) => {
        setIsLoading(true);
        setError(null);

        try {
            const updatedTrace = await gpxTraceService.updateTrace(tracePath, updates);
            // Mettre à jour la trace dans la liste locale
            setTraces(prev => prev.map(trace =>
                trace.path === tracePath ? updatedTrace : trace
            ));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour de la trace');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Charger les traces au montage du composant
    useEffect(() => {
        loadTraces();
    }, [loadTraces]);

    return {
        traces,
        isLoading,
        error,
        totalTraces,
        hasMore,
        refreshTraces,
        loadMoreTraces,
        uploadTrace,
        deleteTrace,
        updateTrace
    };
};
